// 阀值设置 /v1/restaurant/{string_id}/info
import { OpenAIOutputItem } from '../../constants/tool-open-ai';
import { RiskEvent, RiskOrder, SourceType } from '../../models';
import type { RiskOrderGroup, RiskOrderMode } from '../../page/restaurant-risk/risk-order-mode';
import { Restaurant } from '../../models/restaurant.entity';
import { getApiLanguageCode } from '../../constants/api-language';
import http, {
    HttpSuccessOrErrorType,
    ListGetRequestParams,
    ListResults,
    MongoWriteResult,
} from '../http'

export default {
    /**
     * 风控阀值设置 
     * @param stringId 
     * @param data 
     * @returns 
     */
    async patch_restaurant_string_id_info(
        stringId: string,
        data: RiskThresholdRequest,
    ): Promise<HttpSuccessOrErrorType<RestaurantInfoUpdateResponse>> {
        return await http.patch(`/restaurant/${stringId}/info`, data)
    },

    /**
     * 即时风控数据
     * @param stringId 
     * @returns 
     */
    async get_restaurant_riskOrder(stringId: string, params: GetRestaurantRiskOrderRequest): Promise<HttpSuccessOrErrorType<GetRestaurantRiskOrderResponse>> {
        return await http.get(`/restaurant/${stringId}/riskOrder`, {
            params
        })
    },


    /**
     * 即时风控数据 编辑状态
     * @param stringId 
     * @param data 
     * @returns 
     */
    async patch_restaurant_risk_handle(stringId: string, data: PatchRestaurantRiskHandleRequest): Promise<HttpSuccessOrErrorType<any>> {
        return await http.patch(`/restaurant/${stringId}/risk/handle`, data)
    },

    /**
     * 获取云端风控数据
     * @param stringId 
     * @returns 
     */
    async get_cloud_restaurant_risk(stringId: string, params: ListGetRequestParams<GetCloudRestaurantRiskRequest>): Promise<HttpSuccessOrErrorType<ListResults<RiskOrder>>> {
        return await http.get(`/cloud/restaurant/${stringId}/risk`, {
            params
        })
    },

    /**
     * 修改云端风控数据状态
     * @param stringId 
     * @returns 
     */
    async patch_cloud_restaurant_risk(stringId: string, data: PatchCloudRestaurantRiskRequest): Promise<HttpSuccessOrErrorType<any>> {
        return await http.patch(`/cloud/restaurant/${stringId}/risk_order`, data)
    },

    /**
     * 智能推荐阀值 
     * @param stringId 
     * @returns 
     */
    async get_restaurant_risk_recommendation(stringId: string): Promise<HttpSuccessOrErrorType<RiskThresholdRecommendationResponse>> {
        return await http.get(`/restaurant/${stringId}/risk/recommend`)
    },

    /** 风控预警分析 */
    async get_restaurant_risk_analysis(stringId: string): Promise<HttpSuccessOrErrorType<GetRestaurantRiskAnalysisResponse>> {
        return await http.get(`/restaurant/${stringId}/risk/analysis`, {
            params: { language: getApiLanguageCode() },
        })
    }
}


//  风控阀值设置 接口入参字段均来自餐厅实体上的风控相关字段
export type RiskThresholdRequest = Pick<Restaurant,
    | 'risk_control_enabled'
    | 'cancel_order_risk_alert_enabled'
    | 'delivery_max_discount_amount'
    | 'delivery_max_discount_enabled'
    | 'delivery_max_discount_mode'
    | 'delivery_max_discount_percent'
    | 'delivery_max_discount_trigger_logic'
    | 'delivery_order_close_check_window_seconds'
    | 'delivery_order_close_check_window_number'
    | 'dine_in_abnormal_item_count_threshold'
    | 'dine_in_max_dining_duration_minutes'
    | 'dine_in_max_discount_amount'
    | 'dine_in_max_discount_enabled'
    | 'dine_in_max_discount_mode'
    | 'dine_in_max_discount_percent'
    | 'dine_in_max_discount_trigger_logic'
    | 'dine_in_max_refund_amount'
    | 'dine_in_max_refund_item_count'
    | 'dine_in_order_close_interval_minutes'
    | 'dine_in_order_close_check_window_number'
    | 'risk_notify_email_enabled'
    | 'risk_notify_email_receiver'
    | 'risk_notify_email_send_mode'
    | 'shift_briefing_whatsapp_enabled'
    | 'risk_notify_whatsapp_enabled'
    | 'risk_notify_whatsapp_receiver'
    | 'risk_notify_whatsapp_prefix'
>;

/** 风控阈值保存时本地同步的处理状态。 */
export type LocalRiskSyncStatus =
    | 'SYNCED'
    | 'FAILED'
    | 'SKIPPED'
    | 'SKIPPED_TUNNEL_UNAVAILABLE'
    | 'SKIPPED_INCOMPATIBLE_VERSION'
    | 'SKIPPED_NO_DATABASE_CONFIG';

/** 风控阈值保存接口在云端成功后返回的本地同步信息。 */
export interface RestaurantRiskInfoUpdateResponse {
    /** 云端更新是否成功。 */
    success: boolean;
    /** 云端更新结果说明。 */
    message: string;
    /** 云端 MongoDB 更新结果。 */
    result: MongoWriteResult;
    /** 是否已同步到本地。 */
    local_sync: boolean;
    /** 本地同步处理状态。 */
    local_sync_status: LocalRiskSyncStatus;
    /** 本地同步错误信息。 */
    local_sync_error: string | null;
    /** 本地同步的附加详情。 */
    local_sync_detail: string | { code?: string } | null;
}

/** 餐厅信息保存接口返回值，兼容普通信息保存和风控阈值保存。 */
export type RestaurantInfoUpdateResponse =
    | MongoWriteResult
    | RestaurantRiskInfoUpdateResponse;

// 即时风控数据接口 入参
export type GetRestaurantRiskOrderRequest = {
    /** 开始时间，格式 YYYY-MM-DD HH:mm（必填） */
    start_time?: string;
    /** 结束时间，格式 YYYY-MM-DD HH:mm（必填） */
    end_time?: string;
    /**
     * 状态 0 未确认 1忽略 2已解决
     */
    status?: number;
    /** 返回风险点列表或按订单聚合的列表。 */
    mode?: RiskOrderMode;
}
// 即时风控数据接口 出参
export type GetRestaurantRiskOrderResponse = {
    riskEvents: RiskEvent[]
    riskOrders: RiskOrder[]
    /** 订单模式下后端返回的订单聚合列表。 */
    orders?: RiskOrderGroup[]
}


// 编辑状态入参
export type PatchRestaurantRiskHandleRequest = {
    /**
     * ORDER: risk_order.id；EVENT: risk_event.id
     */
    ids?: number[];
    /**
     * 操作人
     */
    operator_name: string;
    /**
     * 备注
     */
    remark?: string;
    /**
     * 状态 1忽略 2已解决
     */
    status?: number;
    target_type: SourceType;
    /** 处理范围：RISK 为单个风险点，ORDER 为整单。 */
    scope?: 'RISK' | 'ORDER';
    /** 订单模式下事件风险关联的订单 ID。 */
    event_order_ids?: number[];
}


//  获取云端风控数据 入参
type GetCloudRestaurantRiskRequest = {
    /**
     * 风控事件类型 ：1快速关闭 2取消
     */
    event_types?: string[];
    /**
     * 服务员
     */
    operator_name?: string[];
    /**
    * 风控类型：1最大优惠金额 2最大退菜数量 3最大退菜金额 4点菜数量异常 5用餐时间最大值
    */
    risk_types?: string[];

    /**
     * 桌台
     */
    table_name?: string[];
    /**
     * 订单号（支持模糊匹配，与 operator_name / table_name 同为数组入参）
     * 云端列表 SearchKeyInput 使用此 key，对应库表 order_head_id 模糊查询
     */
    order_no?: string[];
    /**
     * 处理状态筛选：0 待确认 1 忽略 2 已解决（与即时接口、移动端历史 Tab 一致）
     */
    status?: number;
    /**
     * 时间筛选
     */
    time_type: TimeType;
    /**
     * 时间筛选为CUSTOM 则必须
     */
    start_time?: string;
    /**
    * 时间筛选为CUSTOM 则必须
    */
    end_time?: string;
    /** 固定使用 order 模式返回整单聚合数据。 */
    mode?: RiskOrderMode;
}

/**
 * 时间筛选
 */
export enum TimeType {
    Custom = 'CUSTOM',
    Day = 'DAY',
    Month = 'MONTH',
    Year = 'YEAR',
}

/** 云端风控数据修改状态请求参数 */
export type PatchCloudRestaurantRiskRequest = {
    id?: number
    /**
     * 操作人
     */
    operator_name: string;
    /**
     * 备注
     */
    remark?: string;
    /**
     * 状态 1忽略 2已解决
     */
    status?: number;
    target_type: SourceType;
    /** 处理范围：RISK 为单个风险点，ORDER 为整单。 */
    scope?: 'RISK' | 'ORDER';
}

// 智能推荐阈值返回值
export type RiskThresholdRecommendationResponse = RiskThresholdRequest & {
    __meta?: any;
}

// 风控预警分析 返回参数
export interface GetRestaurantRiskAnalysisResponse {
    /**
     * 餐厅基本信息
     */
    restaurant: {
        /** 餐厅名称 */
        name: string;
        /** 分析日期，格式：YYYY-MM-DD */
        date: string;
    };

    /**
     * 今日及本周概要统计
     */
    summary: {
        /** 今日总异常数（规则 + 事件） */
        today_total: number;
        /** 今日规则类异常数量 */
        rule_count: number;
        /** 今日事件类异常数量 */
        event_count: number;
        /** 本周累计异常总数 */
        week_total: number;
        /** 上周累计异常总数 */
        last_week_total: number;
        /** 与上周相比的增长率（百分比数值，如 100 表示增长 100%） */
        compare_last_week_percent: number;
    };

    /**
     * 今日主要风险类型（Top 风险）
     */
    top_risks: {
        /** 规则类异常的高频类型列表 */
        rule_types: string[];
        /** 事件类异常的高频类型列表 */
        event_types: string[];
    };

    /**
     * 异常集中时间段（小时粒度）
     * 格式如：["14:00-15:00"]
     */
    abnormal_patterns: string[];

    /**
     * 高风险事件类型列表（用于展示高风险项）
     */
    high_risk_events: string[];

    /**
     * 今日详细概览
     */
    overview: {
        /** 分析日期，格式：YYYY-MM-DD */
        date: string;
        /** 今日数据 */
        today: {
            /** 今日总异常数 */
            total: number;
            /** 相比昨日的变化百分比（负值表示下降） */
            compare_yesterday_percent: number;
            yesterdayCnt: number;
            /** 规则类异常详情 */
            rule: {
                /** 规则类异常总数 */
                count: number;
                /** 按类型细分的规则异常 */
                breakdown: Array<{
                    /** 异常类型名称，如“最大优惠金额异常” */
                    type: string;
                    /** 该类型的异常次数 */
                    count: number;
                }>;
            };
            /** 事件类异常详情 */
            event: {
                /** 事件类异常总数 */
                count: number;
                /** 按类型细分的事件异常 */
                breakdown: Array<{
                    /** 事件类型名称，如“快速关闭订单” */
                    type: string;
                    /** 该类型的事件次数 */
                    count: number;
                }>;
            };
        };
    };

    /**
     * 异常趋势（按小时分布）
     */
    trend: {
        /** 时间周期，当前固定为 "WEEK" */
        period: 'WEEK';
        /** 按小时段聚合的异常数据 */
        by_hour: Array<{
            /** 时间段，格式如 "14:00-15:00" */
            hour: string;
            /** 该时段规则类异常数 */
            rule: number;
            /** 该时段事件类异常数 */
            event: number;
            /** 该时段总异常数 */
            total: number;
        }>;
        /** 异常高峰时间段列表 */
        peak_time_ranges: string[];
        /** 本周总异常数 */
        current_week_total: number;
        /** 上周总异常数 */
        last_week_total: number;
        /** 与上周相比的增长率（百分比数值） */
        compare_last_week_percent: number;
    };

    /**
     * 异常类型统计分布
     */
    statistics: {
        /** 规则类异常按类型分布 */
        rule_by_type: Array<{
            /** 异常类型名称 */
            type: string;
            /** 出现次数 */
            count: number;
            /** 占比（整数百分比，如 34 表示 34%） */
            percent: number;
        }>;
        /** 事件类异常按类型分布 */
        event_by_type: Array<{
            /** 事件类型名称 */
            type: string;
            /** 出现次数 */
            count: number;
            /** 占比（整数百分比） */
            percent: number;
        }>;
        /** 所有异常类型（规则 + 事件）混合排序后的统计 */
        all_type_stats: Array<{
            /** 异常或事件类型名称 */
            type: string;
            /** 出现次数 */
            count: number;
            /** 占比（整数百分比） */
            percent: number;
        }>;
    };

    /**
     * 高风险案例详情列表
     */
    high_risk_cases: HighRiskCase[];

    /**
     * 阈值调整建议
     */
    thresholdRecommendations: ThresholdRecommendations;

    /**
     * AI 分析结果：纯文本摘要，或 OpenAI Responses API 结构化 output
     */
    ai_analysis: string | OpenAIOutputItem[];
}

export type HighRiskCase = {
    /** 风险等级，如 "MEDIUM" / "HIGH" */
    risk_level: string;
    /** 风险标题 */
    title: string;
    /** 业务场景，如 "堂食" / "外卖" */
    scene: string;
    /** 摘要信息（用于列表展示） */
    summary: string;
    /** 详细描述（含时间、金额、操作人等） */
    description: string;
    /** 建议操作指引 */
    suggest_action: string;
}
export type ThresholdRecommendations = {
    /** 建议标题 */
    title: string;
    /** 算法说明 */
    description: string;
    /** 堂食场景的阈值配置 */
    dine_in: ThresholdConfig;
    /** 外卖场景的阈值配置（当前可能为空） */
    delivery: ThresholdConfig;
    /** 供用户阅读的建议文案列表 */
    readable: string[];
}

/**
 * 阈值配置项（用于堂食/外卖等场景）
 */
interface ThresholdConfig {
    /**
     * 最大优惠金额阈值（单位：€）
     */
    max_discount_amount?: {
        /** 当前设置值 */
        current: number;
        /** AI 推荐值 */
        recommended: number;
    };
    /**
     * 最大优惠比例阈值（单位：百分比，如 30 表示 30%）
     */
    max_discount_percent?: {
        /** 当前设置值 */
        current: number;
        /** AI 推荐值 */
        recommended: number;
    };
}